import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../ui/toast';
import { Dialog } from '../../../../../../ui';
import { TrashIcon } from '../../../../../../ui/icons/icons';
import { deleteJDTemplate, getJDTemplate, getJDTemplateList } from '../../../../../../utils/api_service/JobOpenings';
import { JDTemplate , JobDescription } from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { DIALOG_TYPE } from '../../../../Constants/DialogTypes';

interface LoadJdTemplateProps {
    closeModal: () => void,
    onTemplateSelect?: (template: JDTemplate) => void,
    jobDescription: JobDescription,
    setJobDescription: React.Dispatch<React.SetStateAction<JobDescription>>,
    setJobTitle: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>
}

export const LoadJdTemplate = ({
  closeModal,
  setJobDescription, jobDescription,
  onTemplateSelect,
  setJobTitle,
}: LoadJdTemplateProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [templates, setTemplates] = useState<JDTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<JDTemplate>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 4;

  // const categories = ['All', ...Array.from(new Set(templates.map(t => t.tags)))];

  const getJobDescriptionTemplates = async () => {
    try {
      const response = await getJDTemplateList(searchTerm, currentPage, itemsPerPage);
      if (response.ok) {
        const data = await response.json();
        setTotalCount(data.total_count);
        const formattedTemplates: JDTemplate[] = data.results.map(
          (item: any) => ({
            templateId: item.id,
            templateTitle: item.templateTitle,
            jobTitle: item.jobTitle.jobTitle ?? 'N/A',
            createdAt: item.createdAt,
            tags: item.tags ?? [],
            templateDescription: item.templateDescription ?? 'N/A',
          }),
        );

        setTemplates(formattedTemplates);
      } else {
        toast.error('Error loading templates', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Error loading templates', { position: 'bottom-center' });
    }
  };

  useEffect(() => {
    getJobDescriptionTemplates();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      getJobDescriptionTemplates();
    }, 200);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchTerm, currentPage]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSelectTemplate = async () => {
    if (selectedTemplate) {
      try {
        const response = await getJDTemplate(selectedTemplate.templateId);
        if (response.ok) {
          const data = await response.json();
          data.jobOpeningId = id;

          // Keep jobTitle as the full object (either string or JobTitle object)
          // Don't convert to ID only - keep the full data structure
          setJobDescription((prev) => ({
            ...prev,
            ...data,
            workType: data.workType ?? 'Permanent',
          }));

          let jobTitleValue = data.jobTitle;
          if (jobTitleValue) {
            const jobTitleOption: SelectOptions = {
              label:
                typeof jobTitleValue !== 'string'
                  ? jobTitleValue?.jobTitle
                  : jobTitleValue,
              value:
                typeof jobTitleValue !== 'string'
                  ? jobTitleValue?.id
                  : jobTitleValue,
            };
            setJobTitle(jobTitleOption);
          }

          if (onTemplateSelect) {
            onTemplateSelect(selectedTemplate);
          } else {
            toast.success('Template loaded successfully!');
          }
          closeModal();
        } else {
          toast.error('Error loading template details', { position: 'bottom-center' });
        }
      } catch (error) {
        console.error('Error loading template:', error);
        toast.error('Error loading template details', { position: 'bottom-center' });
        closeModal();
      }
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await deleteJDTemplate(selectedTemplate.templateId);

      if (response.ok) {
        const res = await response.json();

        setSelectedTemplate(undefined);
        setIsModalOpen(false);

        await getJobDescriptionTemplates();

        // Optional success message
        toast.success('Template deleted successfully!', {
          position: 'bottom-center',
        });
      } else {
        toast.error('Failed to delete template', { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting template', { position: 'bottom-center' });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className='flex flex-col h-full max-w-5xl mx-auto'>
      { /* Search and Filters */ }
      <div className='flex flex-col sm:flex-row gap-4 mb-6'>
        <div className='flex-1 relative'>
          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
            <svg className='h-4 w-4 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                  d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/>
            </svg>
          </div>
          <input
              type='text'
              placeholder='Search templates by name, description, or tags...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
          />
        </div>

      </div>

      { /* Results Info */ }
      <div className='flex justify-between items-center mb-4'>
        <p className='text-sm text-gray-600'>
          { totalCount } template{ totalCount !== 1 ? 's' : '' } found
        </p>
        { selectedTemplate && (
          <div className='flex items-center gap-2 text-sm text-indigo-600'>
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
            </svg>
            { selectedTemplate.templateTitle } selected
          </div>
        ) }
      </div>

      { /* Templates Grid */ }
      <div className='flex-1 min-h-0'>
        { templates.length > 0 ? (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
            { templates.map((template) => (
              <div
                  key={template.templateId}
                  className={`relative p-5 bg-white border-2 rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer group ${
                    selectedTemplate?.templateId === template.templateId
                      ? 'border-indigo-500 bg-indigo-50 shadow-md'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
              >
                { /* Template Content */ }
                <div className='flex justify-between items-start mb-3'>
                  <div className='flex-1'>
                    <h3
                        className={`text-lg font-semibold transition-colors ${
                          selectedTemplate?.templateId === template.templateId
                            ? 'text-indigo-900'
                            : 'text-gray-900 group-hover:text-indigo-900'
                        }`}
                    >
                      { template.templateTitle }
                    </h3>
                    <div className='flex items-center gap-2 mt-1'>
                      <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedTemplate?.templateId === template.templateId
                              ? 'bg-indigo-200 text-indigo-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                      >
                        { template.jobTitle }
                      </span>
                    </div>
                  </div>
                  { selectedTemplate?.templateId === template.templateId && (
                    <div className='w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center ml-3'>
                      <svg
                          className='w-4 h-4 text-white'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                      >
                        <path
                            fillRule='evenodd'
                            d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                            clipRule='evenodd'
                        />
                      </svg>
                    </div>
                  ) }
                </div>

                <p className='text-sm text-gray-600 mb-3 line-clamp-2'>
                  { template.templateDescription }
                </p>

                <div className='flex flex-wrap gap-1 mb-3'>
                  { template.tags?.slice(0, 3).map((tag, index) => (
                    <span
                        key={index}
                        className={`px-2 py-1 text-xs rounded-md ${
                          selectedTemplate?.templateId === template.templateId
                            ? 'bg-white text-indigo-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                      { tag }
                    </span>
                  )) }

                  { template.tags && template.tags.length > 3 && (
                    <span className='px-2 py-1 text-xs text-gray-500'>
                      +{ template.tags.length - 3 } more
                    </span>
                  ) }
                </div>

                <div className='flex justify-between items-center text-xs text-gray-500 mb-4'>
                  <span>Created at: { formatDate(template.createdAt) }</span>
                </div>

                { /* Delete Button (Bottom Right) */ }
                <div className='flex justify-end'>
                  <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTemplate(template);
                        setIsModalOpen(true);
                      }}
                      className='flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-700 transition-colors'
                      title='Delete template'
                  >
                    <TrashIcon className='w-4 h-4' />
                    Delete
                  </button>
                </div>
              </div>
            )) }
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center py-12'>
            <svg
                className='w-12 h-12 text-gray-400 mb-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
            <p className='text-gray-500 text-lg font-medium mb-2'>No templates found</p>
            <p className='text-gray-400 text-sm'>
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) }
      </div>

      { /* Pagination */ }
      { totalPages > 1 && (
        <div className='flex items-center justify-between border-t border-gray-200 pt-4 mb-6'>
          <div className='text-sm text-gray-600'>
            Showing { ((currentPage - 1) * itemsPerPage) + 1 } to{ ' ' }
            { Math.min(currentPage * itemsPerPage, totalCount) } of { totalCount } results
          </div>
          <div className='flex items-center gap-2'>
            <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className='px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Previous
            </button>
            { Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === page
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                { page }
              </button>
            )) }
            <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className='px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Next
            </button>
          </div>
        </div>
      ) }

      { /* Action Buttons */ }
      <div className='flex justify-between items-center border-t border-gray-200 pt-4'>
        <button
            onClick={closeModal}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
        >
          Cancel
        </button>
        <button
            onClick={handleSelectTemplate}
            disabled={!selectedTemplate}
            className={`px-6 py-2 text-sm font-medium rounded-md transition-colors ${
              selectedTemplate
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Load Template
        </button>
      </div>
      <Dialog
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title='Are you sure you want to delete this template?'
          dialogType={DIALOG_TYPE.ERROR}
          justify='center'
          confirmButtonLable={'Delete Template'}
          cancelButtonLable='Cancel'
          handleApply={handleDeleteTemplate}
      >
        <p>This action cannot be undone. This template will be permanently removed from your library.</p>
      </Dialog>
    </div>
  );
};
